import {RefObject, useEffect, useRef} from "react";

const SCROLL_INTO_VIEW_GAP_PX = 16;

export const SCROLL_CHROME = {
    attribute: "data-scroll-chrome",
    bottom: "bottom",
    top: "top"
} as const;

export const scrollChromeProps = {
    bottom: {[SCROLL_CHROME.attribute]: SCROLL_CHROME.bottom},
    top: {[SCROLL_CHROME.attribute]: SCROLL_CHROME.top}
} as const;

interface IParams {
    enabled: boolean;
    sectionRef: RefObject<HTMLElement | null>;
}

interface IScrollFrameContext {
    lastBottom: number | null;
    scrollBy: (delta: number) => void;
    visibleBottom: number;
    visibleTop: number;
}

export const useScrollExpandedAreaIntoView = (params: IParams) => {
    const {enabled, sectionRef} = params;
    const enabledRef = useRef(enabled);
    const frameContextRef = useRef<IScrollFrameContext | null>(null);

    useEffect(() => {
        enabledRef.current = enabled;

        if (!enabled) {
            frameContextRef.current = null;
        }
    }, [enabled]);

    const scrollIfNeeded = () => {
        if (!enabledRef.current) return;

        const section = sectionRef.current;

        if (!section) return;

        if (!frameContextRef.current) {
            frameContextRef.current = resolveScrollFrameContext(section);
        }

        scrollSectionIntoView(section, frameContextRef.current);
    };

    return {
        onAnimationComplete: () => {
            scrollIfNeeded();
            frameContextRef.current = null;
        },
        onUpdate: scrollIfNeeded
    };
};

const resolveScrollFrameContext = (section: HTMLElement): IScrollFrameContext => {
    const sectionStyle = getComputedStyle(section);
    const {scrollBy, viewportBottom, viewportTop} = getScrollPort(getNearestScrollParent(section));
    const chromeBottom = resolveStickyChromeBottom();
    const overlayTop = resolveBottomOverlayTop();
    const scrollMarginTop = Number.parseFloat(sectionStyle.scrollMarginTop) || 0;
    const visibleTop = chromeBottom == null ? viewportTop + scrollMarginTop : chromeBottom + SCROLL_INTO_VIEW_GAP_PX;
    const marginBottom = viewportBottom - (Number.parseFloat(sectionStyle.scrollMarginBottom) || 0);
    const visibleBottom = (overlayTop == null ? marginBottom : Math.min(overlayTop, marginBottom)) - SCROLL_INTO_VIEW_GAP_PX;

    return {
        lastBottom: null,
        scrollBy,
        visibleBottom,
        visibleTop
    };
};

const scrollSectionIntoView = (section: HTMLElement, context: IScrollFrameContext) => {
    const sectionRect = section.getBoundingClientRect();
    const topDelta = sectionRect.top - context.visibleTop;
    const hiddenBelow = sectionRect.bottom - context.visibleBottom;
    const lastBottom = context.lastBottom;

    // The first sample is the fully measured height, before the 0 -> height animation.
    // A shrink after that is the animation restarting; both must not scroll.
    if (lastBottom == null || sectionRect.bottom < lastBottom - 1) {
        context.lastBottom = sectionRect.bottom;
        return;
    }

    const growth = sectionRect.bottom - lastBottom;
    const delta = Math.min(Math.max(0, hiddenBelow), growth, Math.max(0, topDelta));

    context.lastBottom = sectionRect.bottom - (delta > 1 ? delta : 0);

    if (delta <= 1) return;

    context.scrollBy(delta);
};

const resolveStickyChromeBottom = () => {
    const nodes = document.querySelectorAll(`[${SCROLL_CHROME.attribute}="${SCROLL_CHROME.top}"]`);
    let chromeBottom: number | null = null;

    for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;

        const stuckBottom = getStuckChromeBottom(node);

        if (stuckBottom == null) continue;

        chromeBottom = chromeBottom == null ? stuckBottom : Math.max(chromeBottom, stuckBottom);
    }

    return chromeBottom;
};

const getStuckChromeBottom = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();

    if (rect.height <= 0) {
        return null;
    }

    const stickyTop = Number.parseFloat(getComputedStyle(element).top) || 0;

    if (rect.top > stickyTop + 1) {
        return null;
    }

    return rect.bottom;
};

const resolveBottomOverlayTop = () => {
    const nodes = document.querySelectorAll(`[${SCROLL_CHROME.attribute}="${SCROLL_CHROME.bottom}"]`);
    let overlayTop: number | null = null;

    for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;

        const rect = node.getBoundingClientRect();

        if (rect.height <= 0) continue;

        overlayTop = overlayTop == null ? rect.top : Math.min(overlayTop, rect.top);
    }

    return overlayTop;
};

const getNearestScrollParent = (element: HTMLElement): HTMLElement | Window => {
    let parent = element.parentElement;

    while (parent && parent !== document.body && parent !== document.documentElement) {
        const {overflowY} = getComputedStyle(parent);
        const isScrollable = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

        // Bind before the content overflows. The first animation frame is often still too short,
        // and a later scroll on window would leave the modal section cut off.
        if (isScrollable) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return window;
};

const isWindowScrollParent = (scrollParent: HTMLElement | Window): scrollParent is Window => {
    return scrollParent === window;
};

const getScrollPort = (scrollParent: HTMLElement | Window) => {
    if (isWindowScrollParent(scrollParent)) {
        return {
            // Numeric scrollBy stays instant on mobile; the options form can stutter.
            scrollBy: (delta: number) => window.scrollBy(0, delta),
            viewportBottom: window.innerHeight,
            viewportTop: 0
        };
    }

    const parentRect = scrollParent.getBoundingClientRect();

    return {
        scrollBy: (delta: number) => scrollParent.scrollBy(0, delta),
        viewportBottom: parentRect.bottom,
        viewportTop: parentRect.top
    };
};
import {RefObject, useEffect, useRef} from "react";

const SCROLL_INTO_VIEW_GAP_PX = 16;

export const SCROLL_CHROME = {
    attribute: "data-scroll-chrome",
    bottom: "bottom",
    top: "top"
} as const;

export const scrollChromeProps = {
    bottom: {[SCROLL_CHROME.attribute]: SCROLL_CHROME.bottom},
    top: {[SCROLL_CHROME.attribute]: SCROLL_CHROME.top}
} as const;

interface IParams {
    enabled: boolean;
    sectionRef: RefObject<HTMLElement | null>;
}

interface IScrollFrameContext {
    lastBottom: number | null;
    scrollBy: (delta: number) => void;
    visibleBottom: number;
    visibleTop: number;
}

export const useScrollExpandedAreaIntoView = (params: IParams) => {
    const {enabled, sectionRef} = params;
    const enabledRef = useRef(enabled);
    const frameContextRef = useRef<IScrollFrameContext | null>(null);

    useEffect(() => {
        enabledRef.current = enabled;

        if (!enabled) {
            frameContextRef.current = null;
        }
    }, [enabled]);

    const scrollIfNeeded = () => {
        if (!enabledRef.current) return;

        const section = sectionRef.current;

        if (!section) return;

        if (!frameContextRef.current) {
            frameContextRef.current = resolveScrollFrameContext(section);
        }

        scrollSectionIntoView(section, frameContextRef.current);
    };

    return {
        onAnimationComplete: () => {
            scrollIfNeeded();
            frameContextRef.current = null;
        },
        onUpdate: scrollIfNeeded
    };
};

const resolveScrollFrameContext = (section: HTMLElement): IScrollFrameContext => {
    const sectionStyle = getComputedStyle(section);
    const {scrollBy, viewportBottom, viewportTop} = getScrollPort(getNearestScrollParent(section));
    const chromeBottom = resolveStickyChromeBottom();
    const overlayTop = resolveBottomOverlayTop();
    const scrollMarginTop = Number.parseFloat(sectionStyle.scrollMarginTop) || 0;
    const visibleTop = chromeBottom == null ? viewportTop + scrollMarginTop : chromeBottom + SCROLL_INTO_VIEW_GAP_PX;
    const marginBottom = viewportBottom - (Number.parseFloat(sectionStyle.scrollMarginBottom) || 0);
    const visibleBottom = (overlayTop == null ? marginBottom : Math.min(overlayTop, marginBottom)) - SCROLL_INTO_VIEW_GAP_PX;

    return {
        lastBottom: null,
        scrollBy,
        visibleBottom,
        visibleTop
    };
};

const scrollSectionIntoView = (section: HTMLElement, context: IScrollFrameContext) => {
    const sectionRect = section.getBoundingClientRect();
    const topDelta = sectionRect.top - context.visibleTop;
    const hiddenBelow = sectionRect.bottom - context.visibleBottom;
    const lastBottom = context.lastBottom;

    // The first sample is the fully measured height, before the 0 -> height animation.
    // A shrink after that is the animation restarting; both must not scroll.
    if (lastBottom == null || sectionRect.bottom < lastBottom - 1) {
        context.lastBottom = sectionRect.bottom;
        return;
    }

    const growth = sectionRect.bottom - lastBottom;
    const delta = Math.min(Math.max(0, hiddenBelow), growth, Math.max(0, topDelta));

    context.lastBottom = sectionRect.bottom - (delta > 1 ? delta : 0);

    if (delta <= 1) return;

    context.scrollBy(delta);
};

const resolveStickyChromeBottom = () => {
    const nodes = document.querySelectorAll(`[${SCROLL_CHROME.attribute}="${SCROLL_CHROME.top}"]`);
    let chromeBottom: number | null = null;

    for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;

        const stuckBottom = getStuckChromeBottom(node);

        if (stuckBottom == null) continue;

        chromeBottom = chromeBottom == null ? stuckBottom : Math.max(chromeBottom, stuckBottom);
    }

    return chromeBottom;
};

const getStuckChromeBottom = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();

    if (rect.height <= 0) {
        return null;
    }

    const stickyTop = Number.parseFloat(getComputedStyle(element).top) || 0;

    if (rect.top > stickyTop + 1) {
        return null;
    }

    return rect.bottom;
};

const resolveBottomOverlayTop = () => {
    const nodes = document.querySelectorAll(`[${SCROLL_CHROME.attribute}="${SCROLL_CHROME.bottom}"]`);
    let overlayTop: number | null = null;

    for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;

        const rect = node.getBoundingClientRect();

        if (rect.height <= 0) continue;

        overlayTop = overlayTop == null ? rect.top : Math.min(overlayTop, rect.top);
    }

    return overlayTop;
};

const getNearestScrollParent = (element: HTMLElement): HTMLElement | Window => {
    let parent = element.parentElement;

    while (parent && parent !== document.body && parent !== document.documentElement) {
        const {overflowY} = getComputedStyle(parent);
        const isScrollable = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

        // Bind before the content overflows. The first animation frame is often still too short,
        // and a later scroll on window would leave the modal section cut off.
        if (isScrollable) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return window;
};

const isWindowScrollParent = (scrollParent: HTMLElement | Window): scrollParent is Window => {
    return scrollParent === window;
};

const getScrollPort = (scrollParent: HTMLElement | Window) => {
    if (isWindowScrollParent(scrollParent)) {
        return {
            // Numeric scrollBy stays instant on mobile; the options form can stutter.
            scrollBy: (delta: number) => window.scrollBy(0, delta),
            viewportBottom: window.innerHeight,
            viewportTop: 0
        };
    }

    const parentRect = scrollParent.getBoundingClientRect();

    return {
        scrollBy: (delta: number) => scrollParent.scrollBy(0, delta),
        viewportBottom: parentRect.bottom,
        viewportTop: parentRect.top
    };
};
